// src/components/TeamManagement.tsx

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  UserPlusIcon, 
  XCircleIcon, 
  UsersIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../lib/apiClient'; // Ensure this path is correct
import StatCard from './StatCard';
import TeamMemberList from './TeamMemberList';
import InviteMemberModal from './InviteMemberModal';

// Define the TeamMember type for clarity
interface TeamMember {
  user_id: string;
  email: string;
  role: string;
  status: 'active' | 'pending_approval';
  joined_at: string;
  last_login: string;
}

const TeamManagement: React.FC = () => {
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch team members with a clear query key
  const { data: teamMembers = [], isLoading, error } = useQuery<TeamMember[]>({
    queryKey: ['teamMembers'],
    queryFn: async () => {
      const data = await apiClient.getTeamMembers();
      // Ensure data is always an array
      return Array.isArray(data) ? data : [];
    }
  });

  // All mutations should invalidate the teamMembers query on success
  const { mutate: approveMember, isPending: isApproving } = useMutation({
    mutationFn: (userId: string) => apiClient.approveTeamMember(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teamMembers'] }),
  });

  const { mutate: removeMember, isPending: isRemoving } = useMutation({
    mutationFn: (userId: string) => apiClient.removeTeamMember(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teamMembers'] }),
  });

  const { mutate: changeRole, isPending: isChangingRole } = useMutation({
    mutationFn: ({ userId, newRole }: { userId: string; newRole: string }) => 
      apiClient.updateTeamMemberRole(userId, newRole),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teamMembers'] }),
  });

  // Handlers with clear confirmation dialogs
  const handleApprove = (userId: string) => {
    if (window.confirm('Are you sure you want to approve this team member?')) {
      approveMember(userId);
    }
  };

  const handleRemove = (userId: string, email: string) => {
    const action = teamMembers.find(m => m.user_id === userId)?.status === 'pending_approval' ? 'reject' : 'remove';
    if (window.confirm(`Are you sure you want to ${action} ${email}?`)) {
      removeMember(userId);
    }
  };

  const handleRoleChange = (userId: string, newRole: string) => {
    if (window.confirm(`Change this user's role to ${newRole}?`)) {
      changeRole({ userId, newRole });
    }
  };

  // This should eventually come from your useAuth hook
  const currentUserIsAdmin = true; 

  // Derived state for stats
  const activeMembersCount = teamMembers.filter(m => m.status === 'active').length;
  const pendingMembersCount = teamMembers.length - activeMembersCount;

  if (isLoading) return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div><span className="ml-2 text-foreground">Loading team members...</span></div>;
  if (error) return <div className="bg-destructive/10 border border-destructive rounded-md p-4"><div className="flex"><XCircleIcon className="h-5 w-5 text-destructive" /><div className="ml-3"><h3 className="text-sm font-medium text-destructive">Error loading team members</h3><div className="mt-2 text-sm text-destructive">{error.message}</div></div></div></div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your team members, approve requests, and control access levels.
          </p>
        </div>
        {currentUserIsAdmin && (
          <button
            onClick={() => setInviteModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <UserPlusIcon className="h-5 w-5" />
            Invite Member
          </button>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <StatCard title="Total Members" value={teamMembers.length} icon={UsersIcon} color="blue" />
        <StatCard title="Active Members" value={activeMembersCount} icon={CheckCircleIcon} color="green" />
        <StatCard title="Pending Approval" value={pendingMembersCount} icon={ClockIcon} color="yellow" />
      </div>

      {/* Member List */}
      <TeamMemberList
        teamMembers={teamMembers}
        onApprove={handleApprove}
        onRemove={handleRemove}
        onRoleChange={handleRoleChange}
        isAdmin={currentUserIsAdmin}
      />

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />

      {/* Loading States */}
      {(isApproving || isRemoving || isChangingRole) && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-3"></div>
            <span>
              {isApproving && 'Approving member...'}
              {isRemoving && 'Removing member...'}
              {isChangingRole && 'Updating role...'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;